// Outbox drain loop.
//
// One writer, one retry path. Items drain strictly in insertion order so a
// track batch recorded before a moment lands before it, and a single failing
// item backs off instead of blocking the queue forever. Media blobs upload
// first and their storage keys are written back into the queued record, so a
// retry after a partial failure never re-uploads a file that already landed.
import { ApiError, apiClient } from "./api";
import {
  backoffMs,
  listOutbox,
  onOutboxChange,
  outboxCount,
  removeOutboxItem,
  saveOutboxItem,
  toMomentMediaInputs,
  type OutboxItem,
  type OutboxMoment,
} from "./outbox";

export type SyncState = "synced" | "syncing" | "pending" | "offline";

export interface SyncStatus {
  state: SyncState;
  pending: number;
}

let status: SyncStatus = { state: "synced", pending: 0 };
const listeners = new Set<(status: SyncStatus) => void>();
let draining = false;
let timer: ReturnType<typeof setTimeout> | null = null;

function emit(next: Partial<SyncStatus>): void {
  status = { ...status, ...next };
  for (const listener of listeners) listener(status);
}

export function getSyncStatus(): SyncStatus {
  return status;
}

export function onSyncStatus(listener: (status: SyncStatus) => void): () => void {
  listeners.add(listener);
  listener(status);
  return () => {
    listeners.delete(listener);
  };
}

function isOnline(): boolean {
  return typeof navigator === "undefined" ? true : navigator.onLine !== false;
}

function restingState(pending: number): SyncState {
  if (pending === 0) return "synced";
  return isOnline() ? "pending" : "offline";
}

function extension(mime: string): string {
  if (mime === "image/png") return "png";
  if (mime === "image/webp") return "webp";
  if (mime === "video/mp4") return "mp4";
  return "jpg";
}

async function sendMoment(item: OutboxMoment): Promise<void> {
  for (const media of item.payload.media) {
    if (media.storageKey || !media.blob) continue;
    const uploaded = await apiClient.uploadMedia(media.blob, `upload.${extension(media.mime)}`);
    media.storageKey = uploaded.key;
    media.width = uploaded.width ?? media.width;
    media.height = uploaded.height ?? media.height;
    // Persist immediately: if the next upload fails, this one is not redone.
    await saveOutboxItem(item);
  }
  await apiClient.createMoment(item.tripId, {
    clientId: item.clientId,
    lat: item.payload.lat,
    lng: item.payload.lng,
    placeName: item.payload.placeName,
    caption: item.payload.caption,
    capturedAt: item.payload.capturedAt,
    privacy: item.payload.privacy,
    media: toMomentMediaInputs(item.payload.media),
  });
}

async function sendItem(item: OutboxItem): Promise<void> {
  if (item.kind === "moment") {
    await sendMoment(item);
    return;
  }
  await apiClient.appendTrack(item.tripId, item.payload.points);
}

/** Drain the queue once. Safe to call concurrently, extra calls no-op. */
export async function drainOutbox(): Promise<void> {
  if (draining) return;
  draining = true;
  try {
    const items = await listOutbox();
    if (items.length === 0) {
      emit({ state: "synced", pending: 0 });
      return;
    }
    if (!isOnline()) {
      emit({ state: "offline", pending: items.length });
      return;
    }

    emit({ state: "syncing", pending: items.length });
    const now = Date.now();
    for (const item of items) {
      if (item.nextAttemptAt > now) continue;
      try {
        await sendItem(item);
        await removeOutboxItem(item.clientId);
      } catch (error) {
        const permanent = error instanceof ApiError && error.isPermanent;
        item.attempts += 1;
        item.lastError = error instanceof Error ? error.message : String(error);
        if (permanent && item.attempts >= 3) {
          // A rejected payload will never be accepted, stop burning battery
          // on it. It stays visible as a pending item in the header.
          item.nextAttemptAt = now + 24 * 60 * 60_000;
        } else {
          item.nextAttemptAt = now + backoffMs(item.attempts);
        }
        await saveOutboxItem(item);
        // Order matters, so stop at the first failure and retry from here.
        break;
      }
    }
    const pending = await outboxCount();
    emit({ state: restingState(pending), pending });
  } finally {
    draining = false;
  }
}

let started = false;

/**
 * Start the background drain. Polls slowly (15 s) rather than tightly, and
 * reacts to the browser coming back online, so a queued trip does not cost
 * meaningful battery while it waits.
 */
export function startSync(): () => void {
  if (started) return () => {};
  started = true;

  const unsubscribeOutbox = onOutboxChange((count) => {
    if (count > 0 && status.state === "synced") emit({ state: restingState(count), pending: count });
    if (count === 0) emit({ state: "synced", pending: 0 });
  });

  const tick = () => {
    void drainOutbox();
    timer = setTimeout(tick, 15_000);
  };
  timer = setTimeout(tick, 500);

  const onOnline = () => void drainOutbox();
  const onOffline = () => emit({ state: status.pending > 0 ? "offline" : "synced" });
  globalThis.addEventListener?.("online", onOnline);
  globalThis.addEventListener?.("offline", onOffline);

  return () => {
    started = false;
    if (timer) clearTimeout(timer);
    timer = null;
    unsubscribeOutbox();
    globalThis.removeEventListener?.("online", onOnline);
    globalThis.removeEventListener?.("offline", onOffline);
  };
}
